/**
 * Первый вход в новый язык тренировки.
 *
 * Язык уже выбран — в шапке или из объяснения закрытых режимов, — поэтому
 * экран спрашивает только остальное: раскладку, ОС, форму клавиатуры и то, с
 * чего начать. Прогресс прежнего языка при этом никуда не девается, и
 * вернуться к нему можно тут же.
 */

import { useState } from 'react';
import type { LanguageCode } from '@type2x/shared-contracts';
import type { Translate } from '@type2x/i18n';
import { Button, Card } from '@type2x/ui';
import { defaultChoice, type OnboardingChoice } from '@type2x/feature-onboarding';

import { KeyboardChoiceFields } from './KeyboardChoiceFields.js';

export interface LanguageSetupScreenProps {
  readonly t: Translate;
  /** Язык, для которого заводится профиль. */
  readonly language: LanguageCode;
  readonly onStart: (choice: OnboardingChoice) => void;
  readonly onCancel: () => void;
}

export function LanguageSetupScreen({
  t,
  language,
  onStart,
  onCancel,
}: LanguageSetupScreenProps): React.JSX.Element {
  const [choice, setChoice] = useState<OnboardingChoice>(defaultChoice(language));

  return (
    <Card title={t('language.setupTitle', { language: t(`onboarding.language.${language}`) })}>
      <p className="t2x-muted">{t('language.setupHint')}</p>

      <KeyboardChoiceFields t={t} choice={choice} onChange={setChoice} />

      <div className="t2x-actions">
        <Button onClick={() => onStart(choice)}>
          {t(choice.start === 'fundamentals' ? 'onboarding.startBasics' : 'onboarding.start')}
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          {t('language.setupCancel')}
        </Button>
      </div>
    </Card>
  );
}
